import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TreeItem, TreeItemButton } from '@air/component-tree';
import { Button } from '@air/primitive-button';
import { Checkbox } from '@air/primitive-checkbox';

import { LocationsFilterBoardTree } from '~/components/Filters/LocationsFilter/LocationsFilterBoardTree';
import { type LocationsFilterLibrary } from '~/components/Filters/LocationsFilter/useLocationsFilterData';
import { useLibraryRootBoards } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { useLocationsFilterStore } from '~/components/v2/store/features/filters/useLocationsFilterStore';
import { Routes } from '~/constants/routes';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { toggleLocationsLibraryAction } from '~/store/filters/actions';
import { selectedLocationsLibrariesSelector } from '~/store/filters/selectors';
import { pushWithExistingQuery } from '~/utils/PathUtils';

export type LocationsFilterLibraryTreeItemProps = {
  library: LocationsFilterLibrary;
  onLibrarySelect?: ({ libraryId }: { libraryId: string }) => void;
  onBoardSelect?: ({ boardId }: { boardId: string }) => void;
};

export const LocationsFilterLibraryTreeItem = memo(
  ({ library, onBoardSelect, onLibrarySelect }: LocationsFilterLibraryTreeItemProps) => {
    const prefetchedBoards = useMemo(() => Array.from(library.boards.values()), [library]);

    const dispatch = useDispatch();
    const selectedLibraryIds = useSelector(selectedLocationsLibrariesSelector);
    const libraryId = useURLLibraryIdSelector();
    const { currentWorkspace } = useCurrentWorkspace();
    const storeTreeItem = useLocationsFilterStore((store) => store.getTreeItem({ id: library.id }));
    // When boards are pre-fetched (search mode), default to expanded; otherwise use store value
    const open = prefetchedBoards.length > 0 ? (storeTreeItem?.open ?? true) : !!storeTreeItem?.open;
    const setTreeItemOpen = useLocationsFilterStore((store) => store.setTreeItem);
    const {
      data: fetchedBoards,
      isLoadingMore,
      hasMore,
      isInitialLoading,
      loadNextPage,
    } = useLibraryRootBoards({
      workspaceId: !prefetchedBoards.length && open ? currentWorkspace?.id : undefined,
      libraryId: library.id,
    });

    const onClick = useCallback(async () => {
      if (libraryId) {
        await pushWithExistingQuery({
          path: Routes.media.all,
        });
      } else {
        dispatch(
          toggleLocationsLibraryAction({
            library: library.id,
          }),
        );
      }

      onLibrarySelect?.({ libraryId: library.id });
    }, [dispatch, library.id, libraryId, onLibrarySelect]);

    const onOpenChange = useCallback(
      (open: boolean) => {
        setTreeItemOpen({ id: library.id, open, touched: true });
      },
      [library.id, setTreeItemOpen],
    );

    const isSelected = useMemo(
      () => libraryId === library.id || selectedLibraryIds.includes(library.id),
      [library.id, libraryId, selectedLibraryIds],
    );

    const handleLoadMore = useCallback(() => {
      loadNextPage();
    }, [loadNextPage]);

    const displayBoards = useMemo(
      () => (prefetchedBoards.length > 0 ? prefetchedBoards : fetchedBoards),
      [prefetchedBoards, fetchedBoards],
    );

    const shouldShowBoards = open && !!displayBoards.length;
    const hasShowMoreButton = !prefetchedBoards.length && open && hasMore && !isInitialLoading;

    return (
      <>
        <TreeItem
          data-id={library.id}
          open={open}
          onOpenChange={onOpenChange}
          prefix={
            <div
              className="flex size-6 items-center justify-center rounded-4 bg-grey-4"
              style={{ backgroundColor: library.color?.backgroundHex }}
            >
              {library.icon ?? '📚'}
            </div>
          }
          suffix={<Checkbox defaultChecked={false} checked={isSelected} onCheckedChange={onClick} />}
        >
          <TreeItemButton onClick={onClick}>{library.title}</TreeItemButton>
        </TreeItem>
        {shouldShowBoards && (
          <div className="ml-7">
            <LocationsFilterBoardTree boards={displayBoards} locked={isSelected} onBoardSelect={onBoardSelect} />

            {hasShowMoreButton && (
              <Button
                appearance="ghost"
                color="grey"
                data-testid="LOCATIONS_FILTER_SHOW_MORE"
                onClick={handleLoadMore}
                size="small"
              >
                {isLoadingMore ? 'Loading...' : 'Show more'}
              </Button>
            )}
          </div>
        )}
      </>
    );
  },
);

LocationsFilterLibraryTreeItem.displayName = 'LocationsFilterLibraryTreeItem';
